import React, { useEffect, useMemo, useState } from "react";
import useIsNarrow from "../../helpers/useIsNarrow";

/**
 * A wide grid on desktop, a card list on phones / small tablets (below `breakpoint`, default 768px).
 * - renderGrid(): the desktop grid (rendered as it is).
 * - records / renderCard(record) / keyOf(record, index): the cards, with a search box (searchText(record) = the text searched)
 *   and "Show more" paging (pageSize per step).
 */
export default function ResponsiveRecords({
  records,
  renderGrid,
  renderCard,
  keyOf,
  searchText,
  pageSize = 20,
  breakpoint = 768,
  emptyText = "Nothing to show.",
  searchPlaceholder = "Search",
}) {
  const narrow = useIsNarrow(breakpoint);
  const [search, setSearch] = useState("");
  const [shownCount, setShownCount] = useState(pageSize);

  useEffect(() => setShownCount(pageSize), [records, search, pageSize]);

  const matching = useMemo(() => {
    const text = search.trim().toLowerCase();
    if (!text || !searchText) return records;
    return records.filter((r) =>
      String(searchText(r) ?? "")
        .toLowerCase()
        .includes(text),
    );
  }, [records, search, searchText]);

  if (!narrow) return renderGrid();

  const shown = matching.slice(0, shownCount);
  return (
    <div className="rr-cards">
      {searchText && (
        <input
          type="search"
          className="form-control form-control-sm rr-cards__search"
          placeholder={searchPlaceholder}
          aria-label={searchPlaceholder}
          value={search}
          onChange={(e) => setSearch(e.target.value)}
        />
      )}
      <p className="rr-cards__count">
        {matching.length === records.length ? `${records.length} record${records.length === 1 ? "" : "s"}` : `${matching.length} of ${records.length} records`}
      </p>
      {!shown.length && <p className="rr-cards__empty">{emptyText}</p>}
      <ul className="rr-cards__list">
        {shown.map((r, i) => (
          <li key={keyOf(r, i)}>{renderCard(r)}</li>
        ))}
      </ul>
      {matching.length > shown.length && (
        <button type="button" className="btn btn-outline-secondary btn-sm rr-cards__more" onClick={() => setShownCount((n) => n + pageSize)}>
          Show more ({matching.length - shown.length} left)
        </button>
      )}
    </div>
  );
}
